import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listPortalPurchaseOrders, normalizePortalPurchaseOrdersList, uploadPortalPurchaseOrder } from '@/services/portalPurchaseOrders';
import { FileText, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function rowKey(row, i) {
  return row?.id ?? row?._id ?? row?.reference ?? `po-${i}`;
}

function fileLabel(row) {
  return (
    row.file_name ??
    row.filename ??
    row.document_name ??
    row.original_filename ??
    '—'
  );
}

export default function PurchaseOrders() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadMsg, setUploadMsg] = useState('');
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await listPortalPurchaseOrders();
      setList(normalizePortalPurchaseOrdersList(data));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
      setError(typeof msg === 'string' ? msg : 'Satın alma siparişleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = async (file) => {
    if (!file) return;
    setUploadMsg('');
    setError('');
    setUploading(true);
    try {
      await uploadPortalPurchaseOrder(file);
      setUploadMsg('Dosya yüklendi.');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
      setError(typeof msg === 'string' ? msg : 'Yükleme başarısız.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-purchase-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Purchase order</h1>
          <p className="text-slate-500 text-sm mt-0.5">PO belgesi portala yüklenir; süper admin panelinden görülebilir.</p>
        </div>
        <Link to="/dashboard" className="text-sm font-medium text-sky-600 hover:text-sky-700">
          Dashboard
        </Link>
      </div>

      {/* Upload */}
      <div
        role="presentation"
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(false); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrag(false);
          const f = e.dataTransfer?.files?.[0];
          if (f) onFile(f);
        }}
        className={`rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          drag ? 'border-sky-500 bg-sky-50/50' : 'border-slate-200 bg-white'
        }`}
        data-testid="po-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          data-testid="po-file-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-700 mb-1">Dosyayı sürükleyip bırakın veya seçin</p>
        <p className="text-xs text-slate-500 mb-4">PDF, Word, Excel veya görsel (max. sunucu limitine tabi)</p>
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          className="bg-slate-900 text-white border-slate-900 hover:bg-slate-800 hover:text-white"
          data-testid="po-choose-file"
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Yükleniyor…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Dosya seç
            </>
          )}
        </Button>
      </div>

      {uploadMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg px-4 py-3" data-testid="po-upload-success">
          {uploadMsg}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="po-error">
          {error}
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Yüklenen purchase order kayıtları</h2>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-white rounded-xl border border-slate-200 animate-pulse" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Henüz kayıt yok. Yukarıdan PO dosyası yükleyin.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full" data-testid="purchase-orders-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Currency</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Dosya</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Yüklenme</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {list.map((o, i) => (
                  <tr key={rowKey(o, i)} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4 text-sm font-semibold text-slate-900">
                      {o.reference ?? o.order_reference ?? o.po_reference ?? o.id ?? o._id ?? '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600 capitalize">{o.status ?? '—'}</td>
                    <td className="px-5 py-4 text-sm text-slate-900 text-right tabular-nums">
                      {o.total_amount != null ? Number(o.total_amount).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">{o.currency ?? '—'}</td>
                    <td className="px-5 py-4 text-sm text-slate-600 max-w-[200px] truncate" title={fileLabel(o)}>
                      {fileLabel(o)}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      {formatDate(o.uploaded_at ?? o.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
